/**
 * Download a customer's full data export (Phase 3, workplan item 12).
 *
 * Calls GET /api/tenants/{id}/export (backend/routes/tenant_export.py), which
 * returns a zip of everything BOSS holds for that customer. The backend only
 * allows SimpliMeta root administrators; the button that calls this is shown
 * to them alone, so a 403 here means the gate and the backend disagree.
 *
 * Kept out of TenantDetail.js so it can be tested without rendering the page.
 *
 * Returns { ok: true, filename } or { ok: false, message }. Never throws.
 */

const FALLBACK_NAME = (tenantId) => `boss-export-${tenantId}.zip`;

/** Filename from a Content-Disposition header, or null. */
export function filenameFromDisposition(header) {
  if (!header) return null;
  const m = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  return m ? decodeURIComponent(m[1].trim()) : null;
}

/**
 * Read a Blob as text. FileReader rather than Blob.prototype.text, which
 * jsdom does not implement (see the frontend test notes).
 */
export function readBlobText(blob) {
  return new Promise((resolve) => {
    if (!blob) {
      resolve('');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => resolve('');
    reader.readAsText(blob);
  });
}

async function errorMessage(res) {
  if (res.status === 0) return 'Could not reach the server. Check your connection and try again.';
  if (res.status === 403) return 'Only SimpliMeta administrators can export customer data.';
  if (res.status === 404) return 'This customer no longer exists.';
  const text = await readBlobText(res.errorBlob);
  try {
    const detail = JSON.parse(text).detail;
    if (detail) return String(detail);
  } catch {
    /* not JSON */
  }
  return `Export failed (HTTP ${res.status}).`;
}

/** Hand a Blob to the browser as a download. */
export function saveBlob(blob, filename, doc = document, url = URL) {
  const href = url.createObjectURL(blob);
  const a = doc.createElement('a');
  a.href = href;
  a.download = filename;
  doc.body.appendChild(a);
  a.click();
  doc.body.removeChild(a);
  url.revokeObjectURL(href);
}

/**
 * @param {string} tenantId
 * @param {(tenantId: string) => Promise<{ok, status, blob, errorBlob, getHeader}>} fetchExport
 * @param {(blob: Blob, filename: string) => void} [save]
 */
export async function downloadTenantExport(tenantId, fetchExport, save = saveBlob) {
  let res;
  try {
    res = await fetchExport(tenantId);
  } catch {
    return { ok: false, message: 'Could not reach the server. Check your connection and try again.' };
  }
  if (!res || !res.ok || !res.blob) {
    return { ok: false, message: await errorMessage(res || { status: 0 }) };
  }
  const filename = filenameFromDisposition(res.getHeader && res.getHeader('Content-Disposition'))
    || FALLBACK_NAME(tenantId);
  save(res.blob, filename);
  return { ok: true, filename };
}
